import React from 'react'
import {
  Heading,
  Section,
  Placeholder,
  H2,
  H3,
  ContainerLg,
  Lead
} from '../../GlobalStylesNew'
import { Row, Col } from 'reactstrap'
import { useStaticQuery, graphql } from 'gatsby'
import Image from 'gatsby-image'
import { prop } from 'ramda'
import styled from 'styled-components'
import theme from '../../theme'
import { ButtonLink } from '../../common/Links'
import gtmEvent from '../../../utils/gtm'

export const AppSection = styled(Section)`
  background-color: #f4f6f8;
  padding: 50px 0 0px;
  text-align: left;
  @media screen and (min-width: 768px) {
    padding: 100px 0 0;
  }
  @media screen and (min-width: 1024px) {
    padding: 150px 0 0;
  }
`
export const CustomHeading = styled(Heading)`
  font-size: 40px;
  line-height: 1;
  @media screen and (min-width: 768px) {
    font-size: 60px;
  }
`

export const CardText = styled.div`
  padding: 30px 0 100px;
  text-align: center;
  position: relative;
  z-index: 10;
  p {
    margin-bottom: 50px;
  }
  * {
    color: ${p => p.color || p.theme.colors.navy};
  }

  @media screen and (min-width: 768px) {
    padding: 30px 0;
    text-align: left;
    * {
      text-align: left;
    }
  }
`
export const CardTextReverse = styled.div`
  padding: 30px 0 50px;
  text-align: center;
  p {
    margin-bottom: 50px;
  }
  * {
    color: ${p => p.color || p.theme.colors.navy};
  }

  @media screen and (min-width: 768px) {
    padding: 30px 0;
    text-align: right;
    * {
      text-align: right;
    }
  }
`
export const ImgWrapper = styled.div`
  position: relative;
  display: block;
  width: 186px;
  padding: 0;
  margin: 0 auto 20px;
  @media screen and (min-width: 768px) {
    width: 286px;
    margin: 50px 0;
    float: right;
  }
  @media screen and (min-width: 1024px) {
    width: 340px;
    margin: 0 60px 100px 0;
  }
  &:before {
    display: block;
    content: '';
    padding-top: 200%;
  }
  &:after {
    content: '';
    position: absolute;
    left: 0px;
    top: 0px;
    width: 100%;
    height: 100%;
    z-index: 100;
  }
  .gatsby-image-wrapper {
    position: absolute !important;
    top: 50%;
    left: 50%;
    transform: translate(-55%, -48%) scale(1.9);
    width: 100%;
    height: 100%;
    border: 0;
    @media screen and (max-width: 350px) {
      transform: translate(-50%, -50%) scale(1.7);
    }
  }
  &.img-wrapper-reverse {
    @media screen and (min-width: 768px) {
      float: left;
    }
    @media screen and (min-width: 1024px) {
      margin-left: 60px;
    }
  }
`

const handleButtonClick = ({ category, label, action }) => {
  gtmEvent({
    event: 'index-page-click',
    indexPageCategory: category,
    indexPageLabel: label,
    indexPageAction: action
  })
}

const App = props => {
  const data = useStaticQuery(graphql`
    query {
      profile: file(name: { eq: "app_profile2" }) {
        childCloudinaryAsset {
          fluid {
            ...CloudinaryAssetFluid
          }
        }
      }
      events: file(name: { eq: "app_events" }) {
        childCloudinaryAsset {
          fluid {
            ...CloudinaryAssetFluid
          }
        }
      }
      calendar: file(name: { eq: "app_calendar" }) {
        childCloudinaryAsset {
          fluid {
            ...CloudinaryAssetFluid
          }
        }
      }
      payments: file(name: { eq: "app_payments" }) {
        childCloudinaryAsset {
          fluid {
            ...CloudinaryAssetFluid
          }
        }
      }
    }
  `)

  return (
    <AppSection id="app">
      <ContainerLg className="container-xl">
        <H2 className="text-center" color={theme.colors.navy}>
          PGA Coach App
        </H2>
        <Row className="align-items-center">
          <Col md="6" lg="5" xl="4" xs="12" className="offset-lg-2">
            <ImgWrapper>
              {prop('profile', data) ? (
                <Image fluid={data.profile.childCloudinaryAsset.fluid} />
              ) : (
                <Placeholder />
              )}
            </ImgWrapper>
          </Col>
          <Col md="5" lg="4" xs="12">
            <CardText>
              <H3>PGA Coach Profile</H3>
              <Lead>
                We provide the SEO, web traffic and potential students so you
                can focus on your students. <br />
                <br />
                And when used in conjunction with PGA Calendar, students can
                book time with you directly on PGA.com
              </Lead>
              <ButtonLink
                url="https://my.pga.com/pga-coach/profile"
                bgColor={theme.colors.gold}
                borderColor={theme.colors.gold}
                color={theme.colors.white}
                bgHoverColor={theme.colors.darkGold}
                borderHoverColor={theme.colors.darkGold}
                btnStyle="primary"
                padding="14px 30px"
                aria-label="Create your coach profile"
                onClick={() =>
                  handleButtonClick({
                    category: 'create-profile-cta',
                    label: 'create-your-coach-profile',
                    action: 'click-create-profile-cta'
                  })
                }
              >
                Create Your Coach Profile
              </ButtonLink>
            </CardText>
          </Col>
        </Row>
        <Row className="align-items-center">
          <Col md="6" lg="4" xs="12" className="order-md-12">
            <ImgWrapper className="img-wrapper-reverse">
              {prop('calendar', data) ? (
                <Image fluid={data.calendar.childCloudinaryAsset.fluid} />
              ) : (
                <Placeholder />
              )}
            </ImgWrapper>
          </Col>
          <Col md="6" lg="4" xs="12" className="order-md-1 offset-lg-2 pb-5">
            <CardTextReverse>
              <H3>PGA Calendar</H3>
              <Lead>
                Why does booking lessons have to be so hard? With PGA Calendar,
                reduce the back and forth with your students and become more
                efficient with your time.
              </Lead>
              <ButtonLink
                url="https://my.pga.com/pga-coach/bookings/schedule"
                bgColor={theme.colors.gold}
                borderColor={theme.colors.gold}
                color={theme.colors.white}
                bgHoverColor={theme.colors.darkGold}
                borderHoverColor={theme.colors.darkGold}
                btnStyle="primary"
                padding="14px 30px"
                aria-label="Set up calendar"
                onClick={() =>
                  handleButtonClick({
                    category: 'set-up-calendar-cta',
                    label: 'set-up-calendar',
                    action: 'click-set-up-calendar-cta'
                  })
                }
              >
                Set Up Calendar
              </ButtonLink>
            </CardTextReverse>
          </Col>
        </Row>
        <Row className="align-items-center">
          <Col md="6" lg="5" xl="4" xs="12" className="offset-lg-2">
            <ImgWrapper>
              {prop('events', data) ? (
                <Image fluid={data.events.childCloudinaryAsset.fluid} />
              ) : (
                <Placeholder />
              )}
            </ImgWrapper>
          </Col>
          <Col md="5" lg="4" xs="12">
            <CardText>
              <H3>PGA Group Events</H3>
              <Lead>
                Create an engaging group event for your local community. We’ll
                create a marketing page for you on pga.com and handle all the
                registrations.{' '}
              </Lead>
              <ButtonLink
                url="https://my.pga.com/pga-coach/bookings/events"
                bgColor={theme.colors.gold}
                borderColor={theme.colors.gold}
                color={theme.colors.white}
                bgHoverColor={theme.colors.darkGold}
                borderHoverColor={theme.colors.darkGold}
                btnStyle="primary"
                padding="14px 30px"
                aria-label="Create a group event"
                onClick={() =>
                  handleButtonClick({
                    category: 'create-group-event-cta',
                    label: 'create-group-event',
                    action: 'click-create-group-event-cta'
                  })
                }
              >
                Create a Group Event
              </ButtonLink>
            </CardText>
          </Col>
        </Row>
        <Row className="align-items-center">
          <Col md="6" lg="4" xs="12" className="order-md-12">
            <ImgWrapper className="img-wrapper-reverse">
              {prop('payments', data) ? (
                <Image fluid={data.payments.childCloudinaryAsset.fluid} />
              ) : (
                <Placeholder />
              )}
            </ImgWrapper>
          </Col>
          <Col md="6" lg="4" xs="12" className="order-md-1 offset-lg-2">
            <CardTextReverse>
              <H3>PGA Payments</H3>
              <Lead>
                You’re in control. Choose if you want your students to pay
                online or in-person. Either way, we’ve got your covered with
                in-app payment processing.
              </Lead>
            </CardTextReverse>
          </Col>
        </Row>
      </ContainerLg>
    </AppSection>
  )
}

export default App
